import { useAuth } from "@dl/web-core-js/auth";
import { Badge, cn } from "@dl/web-core-js/ui";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  ReplenishmentClient,
  replenishmentKeys,
  type LeadTimeSource,
  type PartReplenishmentDetail,
  type ReplenishmentLineDTO,
} from "../../../api/replenishment";
import { SupplierLink } from "../../../components/SupplierLink";
import { Fact, formatCover, formatQuantity, formatCad, ZONE_TONE, ZoneBadge, ZoneBar } from "./shared";
import { isoDate } from "../../../api/reporting";

/** Unit prices run to fractions of a cent (shrink film by the gram), so up to four decimals. */
const PRICE = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });

/**
 * One material's whole story: where it stands, why the run suggested what it
 * did, and what the next twelve weeks look like.
 *
 * <p>The suggestion is shown as the arithmetic that produced it, line by line,
 * with the real numbers — the same napkin sum the rules are written as. A
 * buyer who can redo the sum trusts it; one who cannot, overrides it.</p>
 *
 * <p>Self-contained: it fetches its own detail, so the part page's tab and the
 * Replenishment screen's drawer share one component and one cache entry.
 * {@code compact} drops the history and trims the lists for the drawer.</p>
 */
export function MaterialStory({ partId, warehouseId = null, compact = false }: { partId: number; warehouseId?: number | null; compact?: boolean }) {
  const { t } = useTranslation();
  const { http } = useAuth();
  const client = useMemo(() => new ReplenishmentClient(http), [http]);
  const { data, isLoading, error } = useQuery({
    queryKey: replenishmentKeys.part(partId, warehouseId),
    queryFn: () => client.part(partId, warehouseId),
    staleTime: 30_000,
  });

  if (error) {
    return (
      <p className="rounded-md bg-risk-soft px-3 py-2 text-[13px] text-risk">
        {t("story.error", "This material's replenishment could not be loaded.")}
      </p>
    );
  }
  if (isLoading || !data) {
    return (
      <div className="flex h-32 items-center justify-center">
        <span className="size-5 animate-spin rounded-full border-2 border-accent border-t-transparent" />
      </div>
    );
  }
  const line = data.line;
  if (!line) {
    return (
      <div className="rounded-md border border-dashed border-line px-4 py-6 text-center text-[13px] text-ink-muted">
        <p className="font-medium text-ink">{t("story.not-planned", "Not planned")}</p>
        <p className="mt-1">
          {t(
            "story.not-planned.detail-plain",
            "Its policy is Not planned, it is not an active purchased part, or replenishment has not run since it became one.",
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Standing line={line} />
      <Breakdown detail={data} line={line} />
      <Projection detail={data} line={line} compact={compact} />
      <OpenLines detail={data} limit={compact ? 5 : undefined} />
      <Reservations detail={data} unit={line.stock_unit_code} limit={compact ? 5 : undefined} />
      {!compact && data.history.length > 1 && <History detail={data} />}
    </div>
  );
}

/** Zone, chips, the bar, and the six numbers a buyer reads first. */
function Standing({ line }: { line: ReplenishmentLineDTO }) {
  const { t } = useTranslation();
  const unit = line.stock_unit_code ?? "";
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <ZoneBadge zone={line.zone} />
        <Badge variant="neutral" size="sm">
          {policyLabel(line.replenishment_policy, t)}
        </Badge>
        {line.is_strategic && (
          <Badge variant="accent" size="sm">
            {t("story.strategic", "Feeds top sellers")}
          </Badge>
        )}
        {line.is_run_out && (
          <Badge variant="warning" size="sm">
            {t("story.run-out", "Run out: every product it feeds is discontinued")}
          </Badge>
        )}
        {line.is_usage_overridden && (
          <Badge variant="info" size="sm">
            {t("story.usage-overridden", "Usage overridden")}
          </Badge>
        )}
        {line.order_unit_code && (
          <Badge variant="neutral" size="sm">
            {line.order_unit_kind === "SHARED"
              ? t("story.shared-unit", "Travels in {{code}}", { code: line.order_unit_code })
              : t("story.per-part-unit", "Bought by the {{code}} of {{size}} {{unit}}", {
                  code: line.order_unit_code,
                  size: formatQuantity(line.stock_units_per_order_unit),
                  unit: line.stock_unit_code ?? "",
                })}
          </Badge>
        )}
        <span className="ml-auto text-[11px] text-ink-faint">
          {line.warehouse_code} ·{" "}
          {line.supplier_name ? <SupplierLink id={line.supplier_id}>{line.supplier_name}</SupplierLink> : t("story.no-supplier", "no supplier")}
        </span>
      </div>
      {line.closure_reason && (
        <p className="rounded-md bg-warn-soft px-3 py-2 text-[12px] text-ink">
          {line.order_by_date
            ? t("story.closure.ahead", "{{reason}} ({{start}} to {{end}}): order by {{date}} to ship before it.", {
                reason: line.closure_reason,
                start: line.closure_start_date ?? "",
                end: line.closure_end_date ?? "",
                date: line.order_by_date,
              })
            : t("story.closure.delayed", "{{reason}} ({{start}} to {{end}}) delays an order placed now: it arrives {{date}}.", {
                reason: line.closure_reason,
                start: line.closure_start_date ?? "",
                end: line.closure_end_date ?? "",
                date: line.arrival_date ?? "",
              })}
        </p>
      )}
      {line.note && <p className="rounded-md bg-surface-sunk px-3 py-2 text-[12px] text-ink-muted">{line.note}</p>}
      <div className="flex flex-col gap-1.5">
        <ZoneBar
          red={line.red_quantity}
          minimum={line.minimum_quantity}
          maximum={line.maximum_quantity}
          position={line.position_quantity}
          after={line.order_quantity > 0 ? line.position_quantity + line.order_quantity + line.drafted_quantity : null}
        />
        {line.maximum_quantity > 0 && (
          <div className="flex justify-between text-[11px] tabular-nums text-ink-faint">
            <span>
              {t("story.red-to", "safety {{value}}", { value: formatQuantity(line.red_quantity) })}
            </span>
            <span>
              {t("story.order-point", "order point {{value}}", { value: formatQuantity(line.minimum_quantity) })}
            </span>
            <span>
              {t("story.maximum", "max {{value}}", { value: formatQuantity(line.maximum_quantity) })}
            </span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-6">
        <Fact label={t("story.on-hand", "On hand")} value={formatQuantity(line.on_hand_quantity)} hint={unit} />
        <Fact label={t("story.on-order", "On order")} value={formatQuantity(line.on_order_quantity)} hint={unit} />
        <Fact label={t("story.reserved", "Reserved")} value={formatQuantity(line.reserved_quantity)} hint={unit} />
        <Fact
          label={t("story.position", "Position")}
          value={<span className={line.position_quantity < 0 ? "text-risk" : undefined}>{formatQuantity(line.position_quantity)}</span>}
          hint={unit}
        />
        <Fact label={t("story.cover", "Cover")} value={formatCover(line.days_of_cover, t)} />
        <Fact
          label={t("story.to-order", "To order")}
          value={<span className={line.order_quantity > 0 ? "text-accent-strong" : undefined}>{formatQuantity(line.order_quantity)}</span>}
          hint={line.order_amount_cad ? formatCad(line.order_amount_cad) : unit}
        />
      </div>
    </section>
  );
}

/**
 * The sum, written out. Each line is "what = how = result", with where every
 * input came from beside it, so an odd suggestion points at its odd input.
 */
function Breakdown({ detail, line }: { detail: PartReplenishmentDetail; line: ReplenishmentLineDTO }) {
  const { t } = useTranslation();
  const q = formatQuantity;
  /** A term after a minus sign: negatives in brackets, so "− (−75)" never reads as "− -75". */
  const term = (value: number | null | undefined) => (value !== null && value !== undefined && Number(value) < 0 ? `(${q(value)})` : q(value));
  const adu = line.average_daily_usage;
  const chosen = line.usage_source === "DERIVED" ? line.derived_usage : line.recorded_usage;
  const windowDays = adu > 0 && !line.is_usage_overridden ? Math.round(chosen / adu) : null;
  const partDays = detail.settings?.safety_days ?? null;
  const baseDays = partDays ?? detail.group_safety_days ?? 0;
  const extra = line.is_strategic ? line.safety_days - baseDays : 0;
  const unit = line.stock_unit_code ?? "";

  /** `unitInResult`: the result already names its unit ("2.33 m / day"), so none is appended after it. */
  const rows: Array<{ label: string; formula: React.ReactNode; result: React.ReactNode; hint?: React.ReactNode; unitInResult?: boolean }> = [];

  rows.push({
    label: t("story.adu", "Daily usage"),
    formula: line.is_usage_overridden
      ? detail.settings?.average_daily_usage_override_reason
        ? t("story.adu.override", "typed override: {{reason}}", { reason: detail.settings.average_daily_usage_override_reason })
        : t("story.adu.override-plain", "typed override")
      : windowDays
        ? `${q(chosen)} ÷ ${String(windowDays)} ${t("story.days", "days")}`
        : t("story.adu.none", "nothing used over the window"),
    result: `${q(adu)}${unit ? ` ${unit}` : ""} / ${t("story.day", "day")}`,
    unitInResult: true,
    hint: t("story.adu.sources", "{{source}} · recorded {{recorded}}, derived {{derived}}", {
      source: line.usage_source === "DERIVED" ? t("story.source.derived", "derived from finished work") : t("story.source.recorded", "recorded issues"),
      recorded: q(line.recorded_usage),
      derived: q(line.derived_usage),
    }),
  });

  if (line.replenishment_policy === "FIXED") {
    rows.push({
      label: t("story.fixed", "Fixed levels"),
      formula: t("story.fixed.detail", "typed on the part: minimum {{min}}, maximum {{max}}", {
        min: q(line.minimum_quantity),
        max: q(line.maximum_quantity),
      }),
      result: "",
    });
  } else if (line.replenishment_policy === "ON_DEMAND" || line.is_run_out) {
    rows.push({
      label: t("story.on-demand", "On demand"),
      formula: t("story.on-demand.detail", "no stock kept: only what open production orders still need is bought"),
      result: "",
    });
  } else {
    rows.push({
      label: t("story.red", "Safety stock (red)"),
      formula: `${q(adu)} × ${String(line.safety_days)} ${t("story.days", "days")}`,
      result: q(line.red_quantity),
      hint:
        extra > 0
          ? t("story.safety.strategic", "{{base}} days ({{from}}) + {{extra}} for top sellers", {
              base: baseDays,
              extra,
              from: partDays !== null ? t("story.from-part", "this part") : t("story.from-group", "its group"),
            })
          : t("story.safety.plain", "{{base}} days, from {{from}}", {
              base: baseDays,
              from: partDays !== null ? t("story.from-part", "this part") : t("story.from-group", "its group"),
            }),
    });
    rows.push({
      label: t("story.yellow", "Lead time (yellow)"),
      formula: `${q(adu)} × ${String(line.lead_time_days)} ${t("story.days", "days")}`,
      result: q(line.yellow_quantity),
      hint:
        line.quoted_lead_time_days != null && line.quoted_lead_time_days !== line.lead_time_days
          ? t("story.yellow.closure", "{{source}}: {{quoted}} days quoted, {{extra}} more for a closure; arrives {{date}}", {
              source: leadSourceLabel(line.lead_time_source, t),
              quoted: line.quoted_lead_time_days,
              extra: line.lead_time_days - line.quoted_lead_time_days,
              date: line.arrival_date ?? "",
            })
          : line.arrival_date
            ? t("story.yellow.arrival", "{{source}}; an order placed now arrives {{date}}", {
                source: leadSourceLabel(line.lead_time_source, t),
                date: line.arrival_date,
              })
            : leadSourceLabel(line.lead_time_source, t),
    });
    rows.push({
      label: t("story.green", "Order cycle (green)"),
      formula: `${q(adu)} × ${String(line.order_cycle_days)} ${t("story.days", "days")}`,
      result: q(line.green_quantity),
      hint: line.minimum_order_quantity
        ? t("story.green.moq", "at least the minimum order of {{moq}}", { moq: q(line.minimum_order_quantity) })
        : line.order_unit_kind === "SHARED"
          ? t("story.green.shared", "the cycle of its truck or container")
          : t("story.green.supplier", "the supplier's cycle, else the purchasing settings' default"),
    });
    rows.push({
      label: t("story.levels", "Order point → maximum"),
      formula: `${q(line.red_quantity)} + ${q(line.yellow_quantity)} → + ${q(line.green_quantity)}`,
      result: `${q(line.minimum_quantity)} → ${q(line.maximum_quantity)}`,
    });
  }

  rows.push({
    label: t("story.position", "Position"),
    formula: `${q(line.on_hand_quantity)} + ${term(line.on_order_quantity)} − ${term(line.reserved_quantity)}`,
    result: <span className={line.position_quantity < 0 ? "text-risk" : undefined}>{q(line.position_quantity)}</span>,
    hint: t("story.position.hint", "on hand + on order − reserved by production orders"),
  });

  const due = line.position_quantity <= line.minimum_quantity || line.position_quantity < 0;
  rows.push({
    label: t("story.suggested", "Suggested"),
    formula: !due
      ? t("story.suggested.not-due", "position is above the order point: nothing yet")
      : line.maximum_quantity > 0
        ? `${q(line.maximum_quantity)} − ${term(line.position_quantity)}`
        : `− ${term(line.position_quantity)}`,
    result: q(line.suggested_quantity),
    hint:
      line.order_unit_kind === "PER_PART" && line.order_unit_code
        ? t("story.suggested.order-unit", "rounded up to whole {{unit}} of {{size}}{{moq}}", {
            unit: line.order_unit_code,
            size: q(line.stock_units_per_order_unit),
            moq: line.minimum_order_quantity ? `, ${t("story.minimum", "minimum")} ${q(line.minimum_order_quantity)}` : "",
          })
        : line.order_multiple || line.minimum_order_quantity
        ? t("story.suggested.rounding", "rounded up to bundles of {{multiple}}{{moq}}", {
            multiple: q(line.order_multiple ?? 1),
            moq: line.minimum_order_quantity ? `, ${t("story.minimum", "minimum")} ${q(line.minimum_order_quantity)}` : "",
          })
        : undefined,
  });
  if (line.drafted_quantity > 0) {
    rows.push({
      label: t("story.drafted", "Already drafted"),
      formula: `${q(line.suggested_quantity)} − ${term(line.drafted_quantity)}`,
      result: q(line.order_quantity),
      hint: t("story.drafted.hint", "draft orders count against what is left to order"),
    });
  }
  if (line.planned_quantity > 0) {
    rows.push({
      label: t("story.planned", "Pipeline lots"),
      formula: t("story.planned.detail", "unstarted lots will need this much; shown, never ordered from"),
      result: <span className="text-ink-muted">{q(line.planned_quantity)}</span>,
    });
  }

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">{t("story.why", "Why this suggestion")}</h3>
      <div className="overflow-hidden rounded-md border border-line">
        {rows.map((row, index) => (
          <div
            key={row.label}
            className={cn("grid grid-cols-[9rem_1fr_auto] items-baseline gap-3 px-3 py-2 text-[12px]", index % 2 === 1 && "bg-surface-sunk/50")}
          >
            <span className="font-medium text-ink">{row.label}</span>
            <span className="min-w-0">
              <span className="tabular-nums text-ink-muted">{row.formula}</span>
              {row.hint && <span className="block text-[11px] text-ink-faint">{row.hint}</span>}
            </span>
            <span className="text-right font-semibold tabular-nums text-ink">
              {row.result}
              {row.result !== "" && unit && !row.unitInResult ? <span className="ml-1 text-[10px] font-normal text-ink-faint">{unit}</span> : null}
            </span>
          </div>
        ))}
      </div>
      {line.unit_price !== null && line.order_quantity > 0 && (
        <p className="text-[11px] text-ink-faint">
          {t("story.price", "{{quantity}} × {{price}} {{currency}} = {{amount}}", {
            quantity: q(line.order_quantity),
            price: PRICE.format(line.unit_price),
            currency: line.currency ?? "",
            amount: formatCad(line.order_amount_cad),
          })}
        </p>
      )}
    </section>
  );
}

/**
 * Twelve weeks of projected stock as bars, one per week, coloured by the zone
 * each week ends in, against dashed lines at the safety stock and order point.
 */
function Projection({ detail, line, compact }: { detail: PartReplenishmentDetail; line: ReplenishmentLineDTO; compact: boolean }) {
  const { t } = useTranslation();
  const weeks = detail.projection;
  if (weeks.length === 0) return null;
  const values = weeks.map((week) => week.end_quantity);
  const top = Math.max(...values, line.maximum_quantity, 1);
  const bottom = Math.min(...values, 0);
  const span = top - bottom || 1;
  const HEIGHT = compact ? 90 : 130;
  const y = (value: number) => ((top - value) / span) * HEIGHT;
  const firstOut = weeks.find((week) => week.zone === "OUT" || week.zone === "RED");
  const shortDate = new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" });

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">{t("story.projection", "Next 12 weeks")}</h3>
        <span className="text-[11px] text-ink-muted">
          {firstOut
            ? t("story.projection.first-red", "reaches {{zone}} the week of {{date}} if nothing more is ordered", {
                zone: firstOut.zone === "OUT" ? t("replenishment.zone.out", "Out") : t("replenishment.zone.red", "Red"),
                date: shortDate.format(new Date(`${firstOut.week_start}T00:00:00`)),
              })
            : t("story.projection.safe", "stays above its safety stock with what is on order")}
        </span>
      </div>
      <div className="relative" style={{ height: HEIGHT + 18 }}>
        {line.red_quantity > 0 && (
          <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-risk/60" style={{ top: y(line.red_quantity) }} />
        )}
        {line.minimum_quantity > 0 && (
          <div className="pointer-events-none absolute inset-x-0 border-t border-dashed border-warn/60" style={{ top: y(line.minimum_quantity) }} />
        )}
        <div className="pointer-events-none absolute inset-x-0 border-t border-line" style={{ top: y(0) }} />
        <div className="absolute inset-x-0 top-0 flex gap-1" style={{ height: HEIGHT }}>
          {weeks.map((week) => {
            const positive = week.end_quantity >= 0;
            const barTop = positive ? y(week.end_quantity) : y(0);
            const barHeight = Math.max(1, Math.abs(y(week.end_quantity) - y(0)));
            return (
              <div
                key={week.week_start}
                className="relative flex-1"
                title={t("story.projection.tooltip", "Week of {{date}}: uses {{demand}}, receives {{receipts}}, ends at {{end}}", {
                  date: shortDate.format(new Date(`${week.week_start}T00:00:00`)),
                  demand: formatQuantity(week.demand),
                  receipts: formatQuantity(week.receipts),
                  end: formatQuantity(week.end_quantity),
                })}
              >
                <div
                  className={cn("absolute inset-x-0.5 rounded-sm", ZONE_TONE[week.zone].solid, week.zone === "GREEN" || week.zone === "OVER" ? "opacity-60" : "opacity-85")}
                  style={{ top: barTop, height: barHeight }}
                />
                {week.receipts > 0 && (
                  <span className="absolute inset-x-0 -top-0.5 text-center text-[9px] font-semibold text-info" style={{ top: Math.max(0, barTop - 12) }}>
                    +
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <div className="absolute inset-x-0 bottom-0 flex gap-1 text-[9px] tabular-nums text-ink-faint">
          {weeks.map((week, index) => (
            <span key={week.week_start} className="flex-1 text-center">
              {index % (compact ? 3 : 2) === 0 ? shortDate.format(new Date(`${week.week_start}T00:00:00`)) : ""}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

function OpenLines({ detail, limit }: { detail: PartReplenishmentDetail; limit?: number }) {
  const { t } = useTranslation();
  if (detail.open_lines.length === 0) return null;
  const shown = limit ? detail.open_lines.slice(0, limit) : detail.open_lines;
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
        {t("story.open-lines", "On order")} <span className="font-normal">({detail.open_lines.length})</span>
      </h3>
      <div className="divide-y divide-line-soft rounded-md border border-line text-[12px]">
        {shown.map((open) => (
          <div key={`${String(open.purchase_order_id)}-${String(open.due_date)}-${String(open.outstanding_quantity)}`} className="flex items-center gap-3 px-3 py-1.5">
            <Link
              to="/purchasing/purchase-orders/$orderId"
              params={{ orderId: String(open.purchase_order_id) }}
              className="font-medium text-accent-strong hover:underline"
            >
              #{open.number}
            </Link>
            <span className="min-w-0 flex-1 truncate text-ink-muted">
              <SupplierLink id={open.supplier_id}>{open.supplier_name}</SupplierLink> · {open.warehouse_code}
            </span>
            {open.status !== "ISSUED" && (
              <Badge variant="neutral" size="sm">
                {open.status === "DRAFT" ? t("story.draft", "Draft") : t("story.awaiting-approval", "Awaiting approval")}
              </Badge>
            )}
            {open.is_on_hold && (
              <Badge variant="warning" size="sm">
                {t("story.on-hold", "on hold")}
              </Badge>
            )}
            {open.is_stale && (
              <Badge variant="neutral" size="sm">
                {t("story.stale", "stale: not counted")}
              </Badge>
            )}
            <span className={cn("tabular-nums", open.is_late && !open.is_stale ? "text-risk" : "text-ink-muted")}>
              {open.due_date ?? ""}
              {open.is_late && !open.is_stale ? ` · ${t("story.late", "late")}` : ""}
            </span>
            <span className="w-20 text-right font-medium tabular-nums text-ink">{formatQuantity(open.outstanding_quantity)}</span>
          </div>
        ))}
        {limit && detail.open_lines.length > limit && (
          <p className="px-3 py-1.5 text-[11px] text-ink-faint">
            {t("story.more", "and {{count}} more", { count: detail.open_lines.length - limit })}
          </p>
        )}
      </div>
    </section>
  );
}

function Reservations({ detail, unit, limit }: { detail: PartReplenishmentDetail; unit: string | null; limit?: number }) {
  const { t } = useTranslation();
  if (detail.reservations.length === 0) return null;
  const shown = limit ? detail.reservations.slice(0, limit) : detail.reservations;
  const total = detail.reservations.reduce((acc, row) => acc + row.remaining_quantity, 0);
  const today = isoDate(new Date());
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">
        {t("story.reservations", "Reserved by production orders")}{" "}
        <span className="font-normal">
          ({detail.reservations.length} · {formatQuantity(total)} {unit ?? ""})
        </span>
      </h3>
      <div className="divide-y divide-line-soft rounded-md border border-line text-[12px]">
        {shown.map((row) => (
          <div key={row.production_order_id} className="flex items-center gap-3 px-3 py-1.5">
            <Link
              to="/production/orders/$orderId"
              params={{ orderId: String(row.production_order_id) }}
              className="font-medium text-accent-strong hover:underline"
            >
              #{row.number}
            </Link>
            <span className="min-w-0 flex-1 truncate text-ink-muted">
              {row.part_number} · {row.warehouse_code}
            </span>
            <span className={cn("tabular-nums", row.due_date && row.due_date < today ? "text-warn" : "text-ink-muted")}>{row.due_date ?? ""}</span>
            <span className="w-20 text-right font-medium tabular-nums text-ink">{formatQuantity(row.remaining_quantity)}</span>
          </div>
        ))}
        {limit && detail.reservations.length > limit && (
          <p className="px-3 py-1.5 text-[11px] text-ink-faint">
            {t("story.more", "and {{count}} more", { count: detail.reservations.length - limit })}
          </p>
        )}
      </div>
    </section>
  );
}

/** Position over the recent runs against its order point: has it been living in red? */
function History({ detail }: { detail: PartReplenishmentDetail }) {
  const { t } = useTranslation();
  const points = detail.history;
  const WIDTH = 600;
  const HEIGHT = 70;
  const values = points.flatMap((point) => [point.position_quantity, point.minimum_quantity]);
  const top = Math.max(...values, 1);
  const bottom = Math.min(...values, 0);
  const span = top - bottom || 1;
  const x = (index: number) => (index / Math.max(1, points.length - 1)) * WIDTH;
  const y = (value: number) => ((top - value) / span) * HEIGHT;
  const path = (pick: (point: (typeof points)[number]) => number) =>
    points.map((point, index) => `${index === 0 ? "M" : "L"}${x(index).toFixed(1)},${y(pick(point)).toFixed(1)}`).join(" ");
  const inRed = points.filter((point) => point.zone === "RED").length;
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-ink-faint">{t("story.history", "Recent runs")}</h3>
        <span className="text-[11px] text-ink-muted">
          {t("story.history.red", "{{red}} of {{total}} days in red", { red: inRed, total: points.length })}
        </span>
      </div>
      <svg viewBox={`0 0 ${String(WIDTH)} ${String(HEIGHT)}`} preserveAspectRatio="none" className="h-[70px] w-full" role="img" aria-label={t("story.history", "Recent runs")}>
        <path d={path((point) => point.minimum_quantity)} fill="none" stroke="var(--warn)" strokeDasharray="4 3" strokeWidth={1} vectorEffect="non-scaling-stroke" />
        <path d={path((point) => point.position_quantity)} fill="none" stroke="var(--ink)" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
        <line x1={0} x2={WIDTH} y1={y(0)} y2={y(0)} stroke="var(--line)" strokeWidth={1} vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="flex justify-between text-[10px] tabular-nums text-ink-faint">
        <span>{points[0]?.as_of_date}</span>
        <span>{points[points.length - 1]?.as_of_date}</span>
      </div>
    </section>
  );
}

export function policyLabel(policy: string, t: (key: string, fallback: string) => string): string {
  switch (policy) {
    case "AUTO":
      return t("story.policy.auto", "Automatic");
    case "FIXED":
      return t("story.policy.fixed", "Fixed levels");
    case "ON_DEMAND":
      return t("story.policy.on-demand", "On demand");
    case "NOT_PLANNED":
      return t("story.policy.not-planned", "Not planned");
    default:
      return policy;
  }
}

export function leadSourceLabel(source: LeadTimeSource, t: (key: string, fallback: string) => string): string {
  switch (source) {
    case "PART_SUPPLIER":
      return t("story.lead.part-supplier", "typed on this part's supplier link");
    case "SUPPLIER_MEASURED":
      return t("story.lead.measured", "measured from the supplier's receipts");
    case "SUPPLIER_FIXED":
      return t("story.lead.fixed", "typed on the supplier");
    default:
      return t("story.lead.default", "the purchasing settings' default");
  }
}
